import { hashFile } from "./fileHash.mjs";
import { sourcePreviewKind } from "./sourcePreview";

const lower = (value) => String(value || "").toLowerCase();

export const EXPECTATION_DOCUMENT_TYPES = ["Purchase invoice", "Sales invoice", "Receipt", "Bank statement", "Monthly report", "Quarterly filing", "Annual report", "Tax document"];
export const DEFAULT_EXPECTED_DOCUMENT_TYPES = ["Purchase invoice", "Sales invoice", "Bank statement"];
export const ACCOUNTING_EVIDENCE_DOCUMENT_TYPES = ["Purchase invoice", "Receipt"];
export const INVOICE_IMPORT_DOCUMENT_TYPES = ["Purchase invoice", "Sales invoice", "Receipt"];

export const accountingEvidenceErrors = (item = {}) => {
  if (!ACCOUNTING_EVIDENCE_DOCUMENT_TYPES.includes(item.documentType)) return [];
  const evidence = item.accountingEvidence || {};
  return [
    !String(evidence.supplier || "").trim() && "Enter the supplier or merchant.",
    !(Number(evidence.grossAmount) > 0) && "Enter a positive document total.",
    !/^[A-Z]{3}$/.test(String(evidence.currency || "")) && "Choose the document currency.",
    !/^\d{4}-\d{2}-\d{2}$/.test(String(evidence.documentDate || "")) && "Enter the document date.",
  ].filter(Boolean);
};

export const classifyDocument = (filename) => {
  const value = lower(filename);
  if (/statement|pasqyr|bank|bkt|paysera|evp/.test(value)) return "Bank statement";
  if (/purchase|supplier|expense|blerje/.test(value)) return "Purchase invoice";
  if (/sales|issued|shitje/.test(value)) return "Sales invoice";
  if (/receipt|fature e vogel|kupon/.test(value)) return "Receipt";
  if (/quarter|q[1-4]|trimester/.test(value)) return "Quarterly filing";
  if (/annual|year|vjetor/.test(value)) return "Annual report";
  if (/month|monthly|mujor/.test(value)) return "Monthly report";
  if (/tax|vat|atk|tvsh/.test(value)) return "Tax document";
  return "Unknown document";
};

export const inferPeriod = (filename) => {
  const value = String(filename || "");
  const iso = value.match(/(20\d{2})[-_. ](0?[1-9]|1[0-2])/);
  if (iso) return `${iso[1]}-${String(iso[2]).padStart(2, "0")}`;
  const quarter = value.match(/(20\d{2}).*?Q([1-4])|Q([1-4]).*?(20\d{2})/i);
  if (quarter) return `Q${quarter[2] || quarter[3]} ${quarter[1] || quarter[4]}`;
  return "";
};

export const documentCoverage = ({ items = [], businessId, period, documentTypes = DEFAULT_EXPECTED_DOCUMENT_TYPES }) => documentTypes.map((documentType) => {
  const matches = items.filter((item) => item.businessId === businessId
    && item.period === period
    && item.documentType === documentType
    && !["Duplicate", "Unsupported", "Failed"].includes(item.status));
  return { documentType, received: matches.length > 0, count: matches.length, itemIds: matches.map((item) => item.id) };
});

export const createIntakeDiagnostic = (item = {}) => ({
  schema: "coetic-x.invoice-studio.intake-diagnostic",
  version: 1,
  createdAt: new Date().toISOString(),
  intake: {
    id: item.id || "",
    mimeType: item.mimeType || "",
    size: item.size || 0,
    fileHashPrefix: item.fileHash?.slice(0, 12) || "",
    documentType: item.documentType || "",
    status: item.status || "",
    source: item.source || "",
    period: item.period || "",
  },
  parser: {
    adapter: item.extraction?.adapter || "",
    parserVersion: item.parserVersion || "unprocessed",
    warningCount: item.extraction?.warnings?.length || (item.warning ? 1 : 0),
    warnings: item.extraction?.warnings || (item.warning ? [item.warning] : []),
    error: item.error || item.parserError || "",
    transactionCount: item.extraction?.transactions?.length || 0,
    sourceLocations: [...new Set((item.extraction?.transactions || []).map((transaction) => transaction.sourceLocation).filter(Boolean))],
  },
  privacy: "Transaction amounts, account identifiers, counterparties, purchase evidence, descriptions, and original file contents are intentionally excluded.",
});

export const expandUploadFiles = async (files) => {
  const expanded = [];
  for (const file of files) {
    if (!file.name.toLowerCase().endsWith(".zip") && file.type !== "application/zip") {
      expanded.push({ file, source: "Manual upload" });
      continue;
    }
    const { default: JSZip } = await import("jszip");
    const zip = await JSZip.loadAsync(file);
    const entries = Object.values(zip.files).filter((entry) => !entry.dir && !entry.name.startsWith("__MACOSX/") && !entry.name.split("/").pop().startsWith("."));
    for (const entry of entries) {
      const blob = await entry.async("blob");
      expanded.push({ file: new File([blob], entry.name.split("/").pop(), { type: blob.type, lastModified: file.lastModified }), source: `ZIP batch: ${file.name}` });
    }
  }
  return expanded;
};

export const createIntakeItem = async ({ file, source, workspaceId, businessId, existingItems = [] }) => {
  const fileHash = await hashFile(file);
  const exactDuplicate = existingItems.find((item) => item.fileHash === fileHash);
  const likelyDuplicate = existingItems.find((item) => item.originalFilename === file.name && item.size === file.size);
  const previewKind = sourcePreviewKind(file);
  const previewUrl = previewKind !== "metadata" && typeof URL !== "undefined" && typeof URL.createObjectURL === "function" ? URL.createObjectURL(file) : "";
  const receivedAt = new Date().toISOString();
  return {
    id: exactDuplicate ? `intake-duplicate-${fileHash.slice(0, 10)}-${Date.now().toString(36)}` : `intake-${fileHash.slice(0, 14)}`,
    workspaceId,
    businessId,
    source,
    originalFilename: file.name,
    mimeType: file.type || "application/octet-stream",
    size: file.size,
    fileHash,
    receivedAt,
    period: inferPeriod(file.name),
    documentType: classifyDocument(file.name, file.type),
    parserVersion: "unprocessed",
    status: exactDuplicate ? "Duplicate" : "Processing",
    statusHistory: exactDuplicate
      ? [{ status: "New", at: receivedAt }, { status: "Duplicate", at: receivedAt }]
      : [{ status: "New", at: receivedAt }, { status: "Processing", at: receivedAt }],
    previewKind,
    previewUrl,
    duplicateOf: exactDuplicate?.id || "",
    warning: exactDuplicate ? `Exact duplicate of ${exactDuplicate.originalFilename}` : likelyDuplicate ? `A file with the same name and size already exists as ${likelyDuplicate.originalFilename}.` : "",
  };
};
